import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./adminlogin.css";
import {
  formatPhoneNumber,
  formatDate,
  formatEventDate,
  formatArrayField,
  fetchWithCORS,
} from "./utils";
import { API_BASE_URL } from "./constants";
import { UserEditModal, DeleteConfirmModal } from "./components/modals";
import { UserManager } from "./components/apps";
import { EventsTab } from "./components/events";
import MobileTabNav from "../../common/MobileTabNav";
import { PendingRequestsTab } from "./components/pendingRequests";
import { ChatTab } from "./components/chat";
import { AccountTab } from "./components/account";
import AppProviders from "../../../context/AppProviders";
import { editSocialUser } from "../../../graphql/client";
import { useAuth } from "../../../context/AuthContext";
import PhotoUploadTab from "./PhotoUploadTab";
import PhotoManagerTab from "./PhotoManagerTab";
import LocationsTab from "./LocationsTab";
import EditEventsTab from "./EditEventsTab";
import RsvpsTab from "./RsvpsTab";
import LeaderboardTab from "./LeaderboardTab";
import RsvpLeaderboardTab from "./RsvpLeaderboardTab";
import TeamTab from "./TeamTab";
import CaptainsAdminTab from "./CaptainsAdminTab";
import ServicesTab from "./ServicesTab";
import SubscriptionsTab from "./SubscriptionsTab";
import IncentivesTab from "./IncentivesTab";
import UsersTab from "./UsersTab";
import FeatureFlagsTab from "./FeatureFlagsTab";
import CopyWeekTab from "./CopyWeekTab";
import RsvpAnswersTab from "./RsvpAnswersTab";
import FlyScraperTab from "./FlyScraperTab";
import HomePhotoControlTab from "./HomePhotoControlTab";
import SmsTab from "./SmsTab";
import LeadsTab from "./LeadsTab";
import PastEventsTab from "./PastEventsTab";
import MailingListTab from "./MailingListTab";

// Helper function has been moved to utils.js

// Helper function has been moved to utils.js

// API_URL and ENABLE_SIGNUP have been moved to constants.js

// Cognito configuration - set these after running the Cognito setup scripts
window.COGNITO_CONFIG = {
  userPoolId: "us-east-1_fc1omeTGh",
  clientId: "54fiucjkk95o6gna3l5l3dnjuo",
  region: "us-east-1",
};

// Admin emails that should always have access regardless of role
const ADMIN_EMAILS = [
  "davidhudman@gmail.com",
  "admin@activebeaches.com",
  "test@activebeaches.com",
  "test@test.com",
  "david@activebeaches.com",
  // Add any admin emails here
];

// Public GroupMe join URL for the regular-user Chat tab. Same value
// that the Join page uses; kept inline so the small ChatLink
// component below doesn't need a separate config import.
const GROUPME_JOIN_URL = "https://groupme.com/join_group/101834766/JW8Wacte";

// Lightweight Chat tab for regular users -- just a launcher for the
// GroupMe group. Real chat moderation (the old admin-only ChatTab
// component) lives inside the Admin tab as "Super Chat".
const ChatLink = () => (
  <div
    style={{
      padding: "32px 16px",
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "16px",
    }}
  >
    <h2 style={{ margin: 0 }}>Active Beaches GroupMe</h2>
    <p style={{ maxWidth: 420, color: "#555", margin: 0 }}>
      All event chat, RSVPs, and photos happen in the GroupMe group.
      Tap below to open or join.
    </p>
    <a
      href={GROUPME_JOIN_URL}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: "inline-block",
        padding: "12px 24px",
        background: "#4a90e2",
        color: "white",
        borderRadius: 4,
        fontWeight: 600,
        textDecoration: "none",
      }}
    >
      Open GroupMe chat
    </a>
  </div>
);

// Sub-tab configs for the Captains + Admin parent tabs. Each parent
// renders these twice: as the launcher grid (when no sub is open) and
// as a small strip at the top of any opened sub-page so the operator
// can switch siblings without bouncing back to the launcher.
// Exported: the dashboard's Apps tab renders these as tiles (tiered
// by role) that deep-link back here via /admin?goto=<id>.
export const CAPTAIN_SUBTABS = [
  { id: "events", label: "Events", icon: "🗓️" },
  { id: "editEvents", label: "Edit Events", icon: "✏️" },
  { id: "rsvps", label: "RSVPs", icon: "🏆" },
  { id: "leaderboard", label: "Leaders", icon: "🥇" },
  { id: "rsvpLeaderboard", label: "RSVPs lb", icon: "🙋" },
  { id: "photoManager", label: "Photos", icon: "🖼️" },
  { id: "photoUpload", label: "Upload", icon: "⬆️" },
  { id: "locations", label: "Locations", icon: "📍" },
];

export const ADMIN_SUBTABS = [
  { id: "pendingRequests", label: "Pending", icon: "📋" },
  { id: "team", label: "Team", icon: "👥" },
  { id: "users", label: "Users", icon: "👤" },
  { id: "captainsAdmin", label: "Captains", icon: "⭐" },
  { id: "subscriptions", label: "Subs", icon: "💳" },
  { id: "incentives", label: "Rewards", icon: "🎁" },
  { id: "pastEvents", label: "Past", icon: "📜" },
  { id: "rsvpAnswers", label: "RSVP Answers", icon: "🙋" },
  { id: "services", label: "Services", icon: "🗂️" },
  { id: "featureFlags", label: "Flags", icon: "🚩" },
  { id: "copyWeek", label: "Copy Week", icon: "📆" },
  { id: "flyScraper", label: "Fly Scraper", icon: "✈️" },
  { id: "homePhotos", label: "Home Photos", icon: "🏠" },
  { id: "sms", label: "SMS", icon: "📱" },
  { id: "leads", label: "Leads", icon: "🔁" },
  { id: "mailingList", label: "Mailing", icon: "✉️" },
  { id: "apps", label: "Apps", icon: "🧩" },
  { id: "superChat", label: "Super Chat", icon: "💬" },
];

/**
 * AdminLogin component now uses the AppProviders context
 */
export default function AdminLogin() {
  const navigate = useNavigate();

  // Use the auth context
  const { isAuthenticated, userInfo, userRole, logout, getAuthHeaders } = useAuth();

  // Debug state to show role info and allow override
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [adminOverride, setAdminOverride] = useState(false);

  // State for the active tab and app
  // Deep link from the dashboard's Apps tab: /admin?goto=<subTabId>
  // lands directly on that captain/admin sub-page (used as initial
  // state only, so goToTopTab's reset behavior is untouched).
  const gotoParam =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("goto")
      : null;
  const gotoAdmin = ADMIN_SUBTABS.some((t) => t.id === gotoParam);
  const gotoCaptain =
    !gotoAdmin && CAPTAIN_SUBTABS.some((t) => t.id === gotoParam);

  // Top-level tab. Values: "calendar" | "chat" | "captains" | "admin" | "account".
  // The "captains" and "admin" tabs are parents that show a launcher
  // grid of buttons (one per page) when no sub-tab is selected.
  const [activeTab, setActiveTab] = useState(
    gotoAdmin ? "admin" : gotoCaptain ? "captains" : "calendar"
  );
  // Sub-tab inside the Captains parent. null = show launcher grid.
  const [activeCaptainsSubTab, setActiveCaptainsSubTab] = useState(
    gotoCaptain ? gotoParam : null
  );
  // Sub-tab inside the Admin parent. null = show launcher grid.
  const [activeAdminSubTab, setActiveAdminSubTab] = useState(
    gotoAdmin ? gotoParam : null
  );
  const [activeApp, setActiveApp] = useState("userManager");

  // Reset the relevant sub-tab to null whenever the user switches
  // top-level tabs (including re-entering a parent they were already
  // on). This is how a fresh tap on "Admin" always lands on the
  // launcher instead of the last sub-page that was open.
  const goToTopTab = (tabId) => {
    setActiveTab(tabId);
    if (tabId === "captains") setActiveCaptainsSubTab(null);
    if (tabId === "admin") setActiveAdminSubTab(null);
  };

  // State for user edit modal
  const [userEditModal, setUserEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userEditFields, setUserEditFields] = useState({});
  const [userEditSaving, setUserEditSaving] = useState(false);
  const [userEditMessage, setUserEditMessage] = useState("");

  // State for delete confirm modal
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState("");

  // True only for full Admins (super-admins). Use this to gate truly
  // admin-only UI like the Pending Requests tab or the user database.
  const isAdminRole = () => {
    if (adminOverride) return true;
    if (userInfo && userInfo.email && ADMIN_EMAILS.includes(userInfo.email)) {
      return true;
    }
    return userRole === "admin";
  };

  // True for both Admins and Captains. Used as the gate for accessing the
  // admin app at all — Captains see a reduced tab list, but they get in.
  const hasAdminAccess = () => {
    if (isAdminRole()) return true;
    return userRole === "captain";
  };

  // Log authentication info for debugging
  useEffect(() => {
    if (isAuthenticated) {
      console.log("Auth Debug Info:", {
        isAuthenticated,
        userRole,
        email: userInfo?.email,
        groups: userInfo?.groups,
        hasAdminAccess: hasAdminAccess(),
      });
    }
  }, [isAuthenticated, userInfo, userRole]);

  // Routing decision (admin app vs user dashboard) lives in
  // Dashboard.js, which reads the role straight from the JWT in
  // sessionStorage. AdminLogin no longer redirects on its own — doing
  // so created an infinite ping-pong with the Dashboard wrapper
  // whenever AuthContext's cached userRole was momentarily stale.
  //
  // We also no longer render a fallback LoginForm here. Dashboard
  // wouldn't mount AdminLogin without a valid token, so the only way
  // we'd hit that fallback is during the brief frame where
  // AuthContext's localStorage-cached isAuthenticated is stale on
  // first render — which is what was sending users to a second login
  // form after /login redirected them. /login is the single login
  // surface; if isAuthenticated really is false here, render nothing
  // and let the next render (after AuthContext hydrates) take over.

  /**
   * Navigate to GraphQL chats
   */
  const navigateToGraphQLChats = () => {
    navigate("/admin/chats");
  };

  /**
   * Handle app change
   * @param {string} app - The app to switch to
   */
  const handleAppChange = (app) => {
    setActiveApp(app);
  };

  /**
   * Handle user edit
   * @param {Object} user - The user to edit
   */
  const handleUserEdit = (user) => {
    setEditingUser(user);

    // Only include fields that actually exist in the database and are shown in the modal
    const fields = {
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      phone: user.phone || "",
      zsex: user.zsex || "",
      groupMeUserId: user.groupMeUserId || "",
      shirtsize: user.shirtsize || "",
      referredBy: user.referredBy || "",
      reInterestedInEvents: user.reInterestedInEvents || "",
      selectedEvents: user.selectedEvents || "",
      zmessage: user.zmessage || "",
      socials: user.socials || "",
      zphone2: user.zphone2 || "",
      // Note: dateAdded is disabled in the modal, so we don't include it here
    };

    setUserEditFields(fields);
    setUserEditMessage("");
    setUserEditModal(true);
  };

  /**
   * Handle user edit field change
   * @param {string} field - The field to change
   * @param {any} value - The new value
   */
  const handleUserEditFieldChange = (field, value) => {
    setUserEditFields((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Save user edit
   */
  const saveUserEdit = async () => {
    setUserEditSaving(true);
    setUserEditMessage("");

    try {
      // Only send fields that have actually changed
      const changedFields = {};
      Object.keys(userEditFields).forEach(field => {
        let originalValue = editingUser[field] || "";
        const newValue = userEditFields[field] || "";
        
        // Handle array fields that might be stored as JSON or comma-separated strings
        if (["reInterestedInEvents", "selectedEvents"].includes(field)) {
          if (Array.isArray(originalValue)) {
            originalValue = originalValue.join(", ");
          } else if (typeof originalValue === "string" && originalValue.startsWith("[")) {
            try {
              const parsed = JSON.parse(originalValue);
              if (Array.isArray(parsed)) {
                originalValue = parsed.join(", ");
              }
            } catch (e) {
              // Keep as string if parsing fails
            }
          }
        }
        
        if (originalValue !== newValue) {
          changedFields[field] = newValue;
        }
      });

      // If no fields changed, don't make the API call
      if (Object.keys(changedFields).length === 0) {
        setUserEditMessage("No changes to save");
        setUserEditSaving(false);
        return;
      }

      // #134 phase 5: edit via graphql editSocialUser (was REST editUser).
      console.log("Saving user fields:", Object.keys(changedFields));
      await editSocialUser(editingUser.userId, changedFields);

      setUserEditMessage("User updated successfully");
      setTimeout(() => {
        setUserEditModal(false);
        setUserEditMessage("");
        // Refresh or update the user list after successful edit
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error("Error saving user:", err);
      setUserEditMessage("Error: " + (err.message || "Failed to update user"));
    } finally {
      setUserEditSaving(false);
    }
  };

  /**
   * Handle close user edit modal
   */
  const handleCloseUserEditModal = () => {
    setUserEditModal(false);
    setUserEditMessage("");
  };

  /**
   * Handle delete user
   * @param {Object} user - The user to delete
   */
  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setDeleteConfirmText("");
    setDeleteMessage("");
    setDeleteConfirmModal(true);
  };

  /**
   * Confirm delete user
   */
  const confirmDeleteUser = async () => {
    if (deleteConfirmText !== "DELETE") {
      setDeleteMessage("Please type DELETE to confirm");
      return;
    }

    setIsDeleting(true);
    setDeleteMessage("");

    try {
      const response = await fetchWithCORS(API_BASE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          type: "deleteUsers",
          userIds: [userToDelete.userId],
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API Error:", response.status, errorText);
        throw new Error(`Failed to delete user (${response.status})`);
      }

      const result = await response.json();
      
      if (result.success) {
        setDeleteMessage("User deleted successfully");
        setTimeout(() => {
          setDeleteConfirmModal(false);
          setDeleteMessage("");
          // Close edit modal if it was open
          setUserEditModal(false);
          setUserEditMessage("");
          // Refresh the user list
          window.location.reload();
        }, 1500);
      } else {
        throw new Error(result.message || "Failed to delete user");
      }
    } catch (err) {
      console.error("Error deleting user:", err);
      setDeleteMessage("Error: " + (err.message || "Failed to delete user"));
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Handle close delete modal
   */
  const handleCloseDeleteModal = () => {
    setDeleteConfirmModal(false);
    setDeleteMessage("");
  };

  /**
   * Handle logout
   */
  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // If AuthContext hasn't hydrated yet on first render, just bail
  // until it does. /login is the only login surface; Dashboard.js
  // gates this component on a real token already.
  if (!isAuthenticated) {
    return null;
  }

  // Debug info component
  const renderDebugInfo = () => {
    if (!showDebugInfo) return null;

    return (
      <div
        className="debug-info"
        style={{
          background: "#f8f9fa",
          padding: "10px",
          margin: "10px 0",
          border: "1px solid #dee2e6",
          borderRadius: "4px",
        }}
      >
        <h4>Admin Authorization Debug</h4>
        <p>User Email: {userInfo?.email || "Unknown"}</p>
        <p>User Role: {userRole || "None"}</p>
        <p>User Groups: {JSON.stringify(userInfo?.groups || [])}</p>
        <p>Has Admin Access: {hasAdminAccess() ? "Yes" : "No"}</p>
        <p>Admin Override: {adminOverride ? "Enabled" : "Disabled"}</p>
        <button
          onClick={() => setAdminOverride(!adminOverride)}
          style={{
            background: adminOverride ? "#28a745" : "#dc3545",
            color: "white",
            border: "none",
            padding: "5px 10px",
            borderRadius: "4px",
            marginRight: "10px",
          }}
        >
          {adminOverride ? "Disable Override" : "Enable Admin Override"}
        </button>
      </div>
    );
  };

  return (
    <div className="admin-login-container">
      <div className="admin-content">
        {/* Debug control button */}
        <div style={{ position: "absolute", top: "10px", right: "10px" }}>
          <button
            onClick={() => setShowDebugInfo(!showDebugInfo)}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontSize: "20px",
            }}
            title="Toggle Debug Info"
          >
            🔧
          </button>
        </div>

        {/* Debug info panel */}
        {renderDebugInfo()}

        <UserEditModal
          isOpen={userEditModal}
          user={editingUser}
          userFields={userEditFields}
          onFieldChange={handleUserEditFieldChange}
          isSaving={userEditSaving}
          message={userEditMessage}
          onSave={saveUserEdit}
          onCancel={handleCloseUserEditModal}
          onDelete={handleDeleteUser}
        />

        <DeleteConfirmModal
          isOpen={deleteConfirmModal}
          user={userToDelete}
          confirmText={deleteConfirmText}
          onConfirmTextChange={setDeleteConfirmText}
          isDeleting={isDeleting}
          deleteMessage={deleteMessage}
          onConfirm={confirmDeleteUser}
          onCancel={handleCloseDeleteModal}
        />

        {/* Top-level tab navigation. Five tabs max:
              - Calendar / Chat / Account: always visible to anyone signed in
              - Captains: visible to captains + admins (parent of an
                inline sub-tab nav)
              - Admin: admin-only (parent; "A:" prefix marks any
                admin-only tab so the operator can tell at a glance)
            Sub-tabs render inline below the parent's content, NOT in
            the bottom mobile nav -- that strip stays at five icons. */}
        <div className="tab-navigation">
          <button
            className={activeTab === "calendar" ? "active" : ""}
            onClick={() => goToTopTab("calendar")}
          >
            Calendar
          </button>
          <button
            className={activeTab === "chat" ? "active" : ""}
            onClick={() => goToTopTab("chat")}
          >
            Chat
          </button>
          {hasAdminAccess() && (
            <button
              className={activeTab === "captains" ? "active" : ""}
              onClick={() => goToTopTab("captains")}
            >
              Captains
            </button>
          )}
          {isAdminRole() && (
            <button
              className={activeTab === "admin" ? "active" : ""}
              onClick={() => goToTopTab("admin")}
            >
              Admin
            </button>
          )}
          <button
            className={activeTab === "account" ? "active" : ""}
            onClick={() => goToTopTab("account")}
          >
            Account
          </button>
        </div>

        {/* Top-level tab content */}
        <div className="tab-content">
          {/* Regular-user tabs */}
          {activeTab === "calendar" && (
            <div className="tab-content">
              <EventsTab canEdit={hasAdminAccess()} />
            </div>
          )}

          {activeTab === "chat" && (
            <div className="tab-content">
              <ChatLink />
            </div>
          )}

          {activeTab === "account" && (
            <div className="tab-content">
              <AccountTab />
            </div>
          )}

          {/* Captains parent — captain + admin. Default state is the
              launcher grid; clicking a card opens that sub-page with
              a back button + a sibling-switching strip at the top. */}
          {activeTab === "captains" && hasAdminAccess() && (
            <>
              {activeCaptainsSubTab === null ? (
                <div className="launcher-grid">
                  {CAPTAIN_SUBTABS.map((st) => (
                    <button
                      key={st.id}
                      type="button"
                      className="launcher-card"
                      onClick={() => setActiveCaptainsSubTab(st.id)}
                    >
                      <span className="launcher-card__icon">{st.icon}</span>
                      <span>{st.label}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className="launcher-back"
                    onClick={() => setActiveCaptainsSubTab(null)}
                  >
                    ← All Captains tabs
                  </button>
                  <div className="tab-navigation tab-navigation--sub">
                    {CAPTAIN_SUBTABS.map((st) => (
                      <button
                        key={st.id}
                        className={
                          activeCaptainsSubTab === st.id ? "active" : ""
                        }
                        onClick={() => setActiveCaptainsSubTab(st.id)}
                      >
                        {st.label}
                      </button>
                    ))}
                  </div>
                  <div className="tab-content">
                    {activeCaptainsSubTab === "events" && (
                      <EventsTab canEdit={true} />
                    )}
                    {activeCaptainsSubTab === "editEvents" && (
                      <EditEventsTab />
                    )}
                    {activeCaptainsSubTab === "rsvps" && <RsvpsTab />}
                    {activeCaptainsSubTab === "leaderboard" && (
                      <LeaderboardTab />
                    )}
                    {activeCaptainsSubTab === "rsvpLeaderboard" && (
                      <RsvpLeaderboardTab />
                    )}
                    {activeCaptainsSubTab === "photoManager" && (
                      <PhotoManagerTab />
                    )}
                    {activeCaptainsSubTab === "photoUpload" && (
                      <PhotoUploadTab />
                    )}
                    {activeCaptainsSubTab === "locations" && <LocationsTab />}
                  </div>
                </>
              )}
            </>
          )}

          {/* Admin parent — admin only. Same launcher pattern as
              Captains. */}
          {activeTab === "admin" && isAdminRole() && (
            <>
              {activeAdminSubTab === null ? (
                <div className="launcher-grid">
                  {ADMIN_SUBTABS.map((st) => (
                    <button
                      key={st.id}
                      type="button"
                      className="launcher-card"
                      onClick={() => setActiveAdminSubTab(st.id)}
                    >
                      <span className="launcher-card__icon">{st.icon}</span>
                      <span>{st.label}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className="launcher-back"
                    onClick={() => setActiveAdminSubTab(null)}
                  >
                    ← All Admin tabs
                  </button>
                  <div className="tab-navigation tab-navigation--sub">
                    {ADMIN_SUBTABS.map((st) => (
                      <button
                        key={st.id}
                        className={
                          activeAdminSubTab === st.id ? "active" : ""
                        }
                        onClick={() => setActiveAdminSubTab(st.id)}
                      >
                        {st.label}
                      </button>
                    ))}
                  </div>
                  <div className="tab-content">
                    {activeAdminSubTab === "pendingRequests" && (
                      <PendingRequestsTab />
                    )}
                    {activeAdminSubTab === "team" && <TeamTab />}
                    {activeAdminSubTab === "captainsAdmin" && (
                      <CaptainsAdminTab />
                    )}
                    {activeAdminSubTab === "subscriptions" && (
                      <SubscriptionsTab />
                    )}
                    {activeAdminSubTab === "incentives" && <IncentivesTab />}
                    {activeAdminSubTab === "users" && <UsersTab />}
                    {activeAdminSubTab === "pastEvents" && <PastEventsTab />}
                    {activeAdminSubTab === "rsvpAnswers" && (
                      <RsvpAnswersTab />
                    )}
                    {activeAdminSubTab === "services" && <ServicesTab />}
                    {activeAdminSubTab === "featureFlags" && (
                      <FeatureFlagsTab />
                    )}
                    {activeAdminSubTab === "copyWeek" && <CopyWeekTab />}
                    {activeAdminSubTab === "flyScraper" && <FlyScraperTab />}
                    {activeAdminSubTab === "homePhotos" && (
                      <HomePhotoControlTab />
                    )}
                    {activeAdminSubTab === "sms" && <SmsTab />}
                    {activeAdminSubTab === "leads" && <LeadsTab />}
                    {activeAdminSubTab === "mailingList" && <MailingListTab />}
                    {activeAdminSubTab === "apps" && (
                      <div className="apps-tab">
                        <div className="apps-navigation">
                          <button
                            className={
                              activeApp === "userManager" ? "active" : ""
                            }
                            onClick={() => handleAppChange("userManager")}
                          >
                            <span className="app-icon">👥</span>
                            <span className="app-name">User Manager</span>
                          </button>
                        </div>
                        {activeApp === "userManager" && (
                          <UserManager onUserEdit={handleUserEdit} />
                        )}
                      </div>
                    )}
                    {activeAdminSubTab === "superChat" && (
                      <ChatTab
                        onNavigateToGraphQLChats={navigateToGraphQLChats}
                      />
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile bottom nav — top-level tabs only. Sub-tabs (the
          captain dashboard and admin dashboard pages) get their own
          inline nav inside the parent tab's content area, so this
          strip stays at five icons no matter the role.
            - Captains tab: visible to captains + admins
            - Admin tab: admin-only ("A:" prefix matches the
              desktop button so it's obvious which one is gated). */}
      <MobileTabNav
        tabs={(() => {
          const allTabs = [
            { id: "calendar", icon: "🗓️", label: "Calendar" },
            { id: "chat", icon: "💬", label: "Chat" },
            { id: "captains", icon: "⭐", label: "Captains", captainPlus: true },
            { id: "admin", icon: "🛡️", label: "Admin", adminOnly: true },
            { id: "account", icon: "👤", label: "Account" },
          ];
          const admin = isAdminRole();
          const captainOrAdmin = hasAdminAccess();
          return allTabs
            .filter((t) => {
              if (t.adminOnly) return admin;
              if (t.captainPlus) return captainOrAdmin;
              return true;
            })
            .map(({ adminOnly, captainPlus, ...rest }) => rest);
        })()}
        activeTab={activeTab}
        onTabChange={goToTopTab}
      />
    </div>
  );
}

/**
 * Wrap the AdminLogin component with AppProviders
 */
export function AdminLoginWithProviders() {
  return (
    <AppProviders>
      <AdminLogin />
    </AppProviders>
  );
}
